import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Subject, Subscription, timer } from "rxjs";
import { switchMap, takeUntil } from "rxjs/operators";
import { decryptResponse, encryptRequest } from "src/app/core/constants/constants";
import { encryptData } from "src/app/pages/card-processing/card.util";
import {
  Authorization,
  BankPaymentRequest,
  ResponseCodes,
  ValidateAccount,
} from "src/app/pages/shared/models/bankPaymentRes";
import { CalculationService } from "src/app/pages/shared/services/calculation.service";
import { PaymentService } from "src/app/pages/shared/services/payment.service";

@Component({
    selector: "app-recurring-details-container",
    templateUrl: "./recurring-details-container.component.html",
    styleUrls: ["./recurring-details-container.component.scss"],
    standalone: false
})
export class RecurringDetailsContainerComponent implements OnInit, OnDestroy {
  tabIndex: number = 0;
  clientId: string;

  clientRef: string;
  decryptedValue: any;
  isInstalment: boolean;

  destroy$ = new Subject();
  cardDetails: any;

  message: string;
  showModal: boolean;

  cardResponse: any;
  otp: any;

  isTokenResponse: boolean;
  subHeader: string;

  showErrorModal: boolean;
  providerResponse: string = "";

  showBankDetails: boolean;

  private apiSubscription: Subscription;

  authorization: Authorization;
  reference: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private calculationService: CalculationService,
    private paymentService: PaymentService,
  ) {}

  ngOnInit() {
    this.route.params.subscribe((param) => {
      this.clientId = param.clientId;
      // this.clientRef = param.ref;
    });

    this.route.queryParams.subscribe((param) => {
      this.decryptedValue = decryptResponse(param.q);
      this.isInstalment = !!param.isInstalment && param.isInstalment === "true";
    });
  }

  changeTab(index: number) {
    this.tabIndex = index;
  }

  makeCardPayment({ req, cardDetails, instalmentReq }) {
    this.cardDetails = cardDetails;

    const cardPaymentRequest = this.isInstalment
      ? this.paymentService.makeInstalmentCardPayment(instalmentReq)
      : this.paymentService.makeRecurringCardPayment(req);

    cardPaymentRequest.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res["responseCode"] == ResponseCodes.success) {
          const instalmentSuccessRes = {
            ...res,
            amount: this.calculationService.calculateTranchePayment(
              this.decryptedValue.amount,
              this.decryptedValue.maxNumberOfPayments,
            ),
            // fee: res['fees'] ?? res['fee'] - this.decryptedValue.amount,
            fee: res["fees"] ?? res["fee"],
            // nextPaymentDate: res["providerExtraInfo"],
            nextPaymentDate: res["nextPaymentDate"],
          };
          this.router.navigate(["subscriptions/success"], {
            queryParams: {
              q: encryptRequest(this.isInstalment ? instalmentSuccessRes : res),
            },
          });
        } else if (res["responseCode"] == ResponseCodes.tokenResponse) {
          this.isTokenResponse = true;
          this.message = res["providerResponse"];
          this.providerResponse = res["responseDescription"];
          this.cardResponse = res;
          this.showModal = true;
        } else {
          this.showErrorModal = true;
          this.subHeader =
            "Transaction unsuccessful, please check the card details and try again";
        }
      },
      error: (err) => {
        let errorMessage =
          err?.error?.responseMessage ?? "An error occured, please try again";
        if (err.error.responseCode === ResponseCodes.validationError) {
          errorMessage = "Card validation error.";
        }
        this.toastr.error(errorMessage);
      },
    });
  }

  async submitOtp(otp) {
    if (otp.length < 6) {
      return;
    }
    const { paymentId, reference } = this.cardResponse;
    const payload = {
      otp,
      paymentId,
      isRecurring: true,
      cardDetails: this.cardDetails,
    };

    const encryptedReq = await encryptData(payload);

    this.paymentService
      .authorizeRecurringPayment(encryptedReq, this.clientId, reference)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (res) => {
          if (res.responseCode == ResponseCodes.success) {
            this.router.navigate(["subscriptions/success"], {
              queryParams: {
                q: encryptRequest(res),
              },
            });
          }

          if (res.responseCode === ResponseCodes.error) {
            this.showErrorModal = true;
            this.subHeader =
              "You entered a wrong OTP, please enter the correct OTP to complete payment";
          }
        },
        error: (err) => {},
      });
  }

  async resendOtp() {
    const reqToEncrypt = {
      reference: this.decryptedValue.planReference,
      amount: this.decryptedValue.amount,
      customerId: this.decryptedValue.email,
      cardDetails: this.cardDetails,
    };

    let otpVa = await encryptData(reqToEncrypt);

    this.paymentService
      .resendOtp(otpVa)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {},
      });
  }

  makeBankPayment(req) {
    const {
      firstName,
      lastName,
      planId,
      email,

      planReference,
      phoneNumber,
      address,
    } = this.decryptedValue;
    const { accountNumber, accountName, bankCode, narration } = req;
    const payload: BankPaymentRequest = {
      customer: {
        firstName,
        lastName,
        email,
        phoneNumber,
        address,
      },
      planReference,
      clientId: this.clientId,
      authorization: {
        accountName,
        accountNumber,
        bankCode,
        narration,
        provider: "eazypay",
      },
      reference: `${Date.now().toString()}${Math.floor(Math.random() * 900)}`,
    };

    this.authorization = payload.authorization;

    const instalmentPayload = {
      customer: {
        subscriberName: firstName + " " + lastName,
        email: email,
        phoneNumber: phoneNumber,
        address: address,
      },
      planReference: planReference,
      channel: "ACCOUNT",
      authorization: {
        accountName,
        accountNumber,
        bankCode,
        narration,
        provider: "eazypay",
      },
      reference: payload.reference,
      clientId: this.clientId,
    };

    const bankAccountPaymentRequest = this.isInstalment
      ? this.paymentService.makeInstalmentBankPayment(instalmentPayload)
      : this.paymentService.makeRecurringBankPayment(payload);

    bankAccountPaymentRequest.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.responseCode === ResponseCodes.success) {
          this.reference = res.reference;
          this.showBankDetails = true;
        }
        // for instalment
        if (res.reference && res.responseCode === ResponseCodes.error) {
          this.reference = res.reference;
          this.showBankDetails = true;
        }
      },
      error: (err) => {
        let errorMessage =
          err?.error?.responseMessage ?? "An error occured, please try again";
        if (err.error.responseCode === ResponseCodes.validationError) {
          errorMessage = "validation error.";
        }
        this.toastr.error(errorMessage);
      },
    });
  }

  confirmTransfer() {
		const payload: ValidateAccount = {
			userId: this.decryptedValue.email,
			paymentReference: this.reference,
			clientId: this.decryptedValue.clientId,
		};

		const validateAccountRequest = this.isInstalment ?
			this.paymentService.validateInstalmentAccount(payload) :
			this.paymentService.validateAccountPayment(payload);
		this.toastr.info("Validating payment...");
		validateAccountRequest.subscribe({
			next: (response) => {
				// Handle the API response
				const subscriptionReq = {
					authorization: this.authorization,
					subscriptionReference: this.reference,
					clientId: this.clientId,
				};
				const instalmentReq = {
					clientId: this.clientId,
					authorization: this.authorization,
					instalmentReference: this.reference,
					instalmentPlanReference: this.decryptedValue.planReference,
				};

				this.startApiCalls(subscriptionReq, instalmentReq);
				// if (response.responseCode === ResponseCodes.success) {
				// 	// this.stopApiCalls();
				// } else if (response.responseCode === ResponseCodes.pendingResponse) {
				// 	this.startApiCalls(subscriptionReq, instalmentReq);
				// 	// this.showStatus();
				// } else {
				// 	this.startApiCalls(subscriptionReq, instalmentReq);
				// }
			},
			error: (error) => {
				let subscriptionReq = {
					authorization: this.authorization,
					subscriptionReference: this.reference,
					clientId: this.clientId,
				};
				const instalmentReq = {
					authorization: this.authorization,
					instalmentReference: this.reference,
					instalmentPlanReference: this.decryptedValue.planReference,
					clientId: this.clientId,
				};
				this.startApiCalls(subscriptionReq, instalmentReq);
				// this.loader = false;
			}
		})
  }
	
  private startApiCalls(subscriptionReq, instalmentReq) {
		this.toastr.info("Processing payment...");
		const request = this.isInstalment ?
		this.paymentService.processInstalmentAccountPayment(instalmentReq) :
		this.paymentService.processSubscription(subscriptionReq)
		this.apiSubscription = timer(0, 30000) // Repeat every 30 seconds
			.pipe(
				switchMap(() => request), // Replace with your API endpoint
			)
			.subscribe({
				next: (response) => {
					// Handle the API response

					if (response.responseCode === ResponseCodes.success) {
						this.stopApiCalls();
						this.router.navigate(["subscriptions/success"], {
							queryParams: {
								q: encryptRequest(response),
							},
						});

						// this.stopApiCalls();
					} else if (response.responseCode === ResponseCodes.pendingResponse) {
						// this.startApiCalls();
						// this.showStatus();
					}
				},
				error:(error) => {
					this.toastr.info("Error processing payment. Ensure you have made the transfer.");
					// this.loader = false;
				}
			});
	}

  private stopApiCalls() {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
  }

  processSubscription(payload) {
    this.paymentService
      .processSubscription(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {},
        error: (err) => {},
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
